import React, { ChangeEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, LoadingButton, TextField } from '@carsys/ui'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Typography
} from '@mui/material'
import Upload from '@mui/icons-material/Upload'
import Delete from '@mui/icons-material/Delete'
import { notifier } from '@cbgms/base/notifier/use-notifier'
import { useFormikContext } from 'formik'
import { useDispatch } from 'react-redux'
import { downloadJSONForm, uploadJSONForm } from '../state/actions'
import { FormValues } from './Form'

const ALLOWED_JSON_TYPES = ['application/json', 'text/json']
const MAX_FILE_SIZE = 5000000
const spacing = 1

const FORM_NAMES: Record<string, string> = {
  lp_simple: 'license plate simple form',
  lp_complex: 'license plate complex form',
  diesel_part_testing: 'Diesel parts testing',
  cleaning_diesel_filter: 'Cleaning Particulate Filter'
}

export const getJSONForms = (t: (key: string) => string) => [
  { value: 'lp_simple', label: t('wholesale.lp_simple_form') },
  { value: 'lp_complex', label: t('wholesale.lp_complex_form') },
  { value: 'diesel_part_testing', label: t('wholesale.diesel_part_testing') },
  { value: 'cleaning_diesel_filter', label: t('wholesale.cleaning_diesel_filter') }
]

interface MessageDialogProps {
  open: boolean
  onClose: () => void
  title: string
  message: string
}

const MessageDialog: React.FC<MessageDialogProps> = ({ open, onClose, title, message }) => {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onClose={onClose} enableBackdropClick scroll='paper'>
      <DialogTitle data-testid='message-dialog-title'>{title}</DialogTitle>
      <DialogContent dividers data-testid='message-dialog-content'>
        <DialogContentText component='div'>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color='primary' variant='contained' onClick={onClose} data-testid='message-dialog-ok-button'>
          {t('global.ok')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const JSONForm: React.FC = () => {
  const { t } = useTranslation()
  const JSON_FORMS = getJSONForms(t)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const { setFieldValue, setFieldTouched, values } = useFormikContext<FormValues>()
  const dispatch = useDispatch()

  const [selectedForm, setSelectedForm] = useState<string>('')
  const [fileName, setFileName] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [showTemplateConfirmDialog, setShowTemplateConfirmDialog] = useState(false)

  const handleFormSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSelectedForm(value)
    setFileName('')
    setUploadSuccess(false)
    setFieldValue('JSONFormUploadSuccess', false)
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files as FileList

    if (files.length > 0) {
      const selectedFile = files[0]
      const fileExtension = selectedFile.name.toLowerCase().split('.').pop()
      if (!ALLOWED_JSON_TYPES.includes(selectedFile.type) && fileExtension !== 'json') {
        event.target.value = ''
        setFileName('')
        return notifier.error(t('wholesale.json_form_file_type_error'))
      }

      if (selectedFile.size > MAX_FILE_SIZE) {
        event.target.value = ''
        setFileName('')
        return notifier.error(t('wholesale.json_form_file_size_error'))
      }

      setFileName(selectedFile.name)
      setUploadSuccess(false)
      setFieldValue('JSONFormFile', selectedFile, true)
      setFieldValue('JSONFormName', FORM_NAMES[selectedForm])
      setFieldTouched('JSONFormFile', true, false)

      if (values.UUID && selectedFile) {
        setIsUploading(true)
        try {
          await dispatch(uploadJSONForm(values.UUID, selectedFile, FORM_NAMES[selectedForm]))
          setUploadSuccess(true)
          setFieldValue('JSONFormUploadSuccess', true)
          notifier.success(t('wholesale.json_form_upload_success'))
        } catch (error) {
          console.error('Upload failed', error)
          setUploadSuccess(false)
          setFieldValue('JSONFormUploadSuccess', false)
          notifier.error(t('wholesale.json_form_upload_error'))
        } finally {
          setIsUploading(false)
        }
      }
    } else {
      setFileName('')
    }
  }

  const downloadFile = (blob: Blob, filename: string) => {
    const url = globalThis.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    globalThis.URL.revokeObjectURL(url)
  }

  const handleDownloadFile = async () => {
    if (!selectedForm || !values.UUID) return

    const formName = FORM_NAMES[selectedForm]
    const wholesalerUUID = values.UUID

    setIsDownloading(true)
    try {
      const blob = await dispatch(downloadJSONForm(wholesalerUUID, formName))
      if (blob && blob.size > 0) {
        downloadFile(blob, `${formName}.json`)
        notifier.success(t('wholesale.json_form_download_success'))
      } else {
        setIsDownloading(false)
        setShowTemplateConfirmDialog(true)
        return
      }
    } catch (error: any) {
      console.error('Download failed', error)
      if (error?.response?.status === 404 || error?.message?.includes('no data')) {
        setIsDownloading(false)
        setShowTemplateConfirmDialog(true)
        return
      } else {
        notifier.error(t('wholesale.json_form_download_error'))
      }
    } finally {
      setIsDownloading(false)
    }
  }

  const handleCloseTemplateDialog = () => {
    setShowTemplateConfirmDialog(false)
  }

  const handleDeleteFile = () => {
    setFileName('')
    setUploadSuccess(false)
    setFieldValue('JSONFormUploadSuccess', false)
    setFieldValue('JSONFormFile', undefined, true)
    setFieldValue('JSONFormName', '', true)
    setFieldTouched('JSONFormFile', false, false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <Box>
      <Stack spacing={spacing} pr={2}>
        <Typography variant='h6'>{t('wholesale.json_forms')}</Typography>

        <TextField select value={selectedForm} onChange={handleFormSelect} label={t('wholesale.form')} fullWidth margin='normal'>
          {JSON_FORMS.map(form => (
            <MenuItem key={form.value} value={form.value}>
              {form.label}
            </MenuItem>
          ))}
        </TextField>

        {selectedForm && (
          <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <LoadingButton
                variant='outlined'
                color='primary'
                onClick={handleDownloadFile}
                loading={isDownloading}
                data-testid='btn-download-json'
              >
                {t('wholesale.download_file')}
              </LoadingButton>
            </Box>

            <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 'bold' }}>
              {t('wholesale.or')}
            </Typography>
            <Typography variant='subtitle2'>{t('wholesale.select_file_to_upload')}</Typography>
            <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 'bold' }}>
              {t('wholesale.accepted_file_extension')}
            </Typography>

            {fileName && (
              <TextField
                value={fileName}
                margin='normal'
                fullWidth
                disabled
                data-testid='selected-file-name'
                InputProps={{
                  endAdornment: uploadSuccess ? (
                    <InputAdornment position='end'>
                      <IconButton onClick={handleDeleteFile} edge='end' size='small' data-testid='btn-delete-file'>
                        <Delete />
                      </IconButton>
                    </InputAdornment>
                  ) : isUploading ? (
                    <InputAdornment position='end' />
                  ) : (
                    <InputAdornment position='end'>
                      <IconButton onClick={handleDeleteFile} edge='end' size='small' data-testid='btn-delete-file'>
                        <Delete />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                component='label'
                variant='outlined'
                startIcon={<Upload />}
                disabled={!!fileName || isUploading}
                data-testid='btn-select-file'
              >
                <input
                  ref={fileInputRef}
                  accept='.json,application/json,text/json'
                  hidden
                  multiple={false}
                  type='file'
                  onChange={handleFileChange}
                  data-testid='file-input'
                  disabled={!!fileName}
                />
                {t('wholesale.upload')}
              </Button>
            </Box>
          </>
        )}
      </Stack>

      <MessageDialog
        open={showTemplateConfirmDialog}
        onClose={handleCloseTemplateDialog}
        title={t('wholesale.download_template_confirm_title')}
        message={t('wholesale.download_template_confirm_message')}
      />
    </Box>
  )
}

export default JSONForm
